import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchLeaderboardData } from './db/dbLeaderboard.js';
import './styles/Leaderboard.css';
import { useWallet } from './WalletContext.js';

const LEADERBOARD_CACHE_KEY = 'leaderboardData';
const LEADERBOARD_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h safety-net (version check is primary)
const LEADERBOARD_NOTICE_HIDE_KEY = 'leaderboardNoticeHidden';

function getCachedLeaderboard() {
  const raw = localStorage.getItem(LEADERBOARD_CACHE_KEY);
  if (!raw) return null;
  try {
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > LEADERBOARD_CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function setCachedLeaderboard(data) {
  localStorage.setItem(LEADERBOARD_CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
}

function Leaderboard() {
  const { walletAddress } = useWallet();
  const [ownerStats, setOwnerStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState({ id: 'points', desc: true });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [showNotice, setShowNotice] = useState(() => {
    if (typeof localStorage === 'undefined') return true;
    return localStorage.getItem(LEADERBOARD_NOTICE_HIDE_KEY) !== '1';
  });
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const isWalletAddress = (s) => /^0x[0-9a-fA-F]{10,}$/.test(s);

  const handleCopyAddress = (address) => {
    navigator.clipboard.writeText(address).catch(() => {});
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 1500);
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      let data = getCachedLeaderboard();
      if (!data) {
        data = await fetchLeaderboardData();
        setCachedLeaderboard(data);
      }

      // Usernames are now resolved by the cron and stored in Firestore.
      // dbLeaderboard.js attaches them directly — no OpenSea API calls needed here.
      const updatedStats = data.ownerStatisticsArray.map(owner => ({
        ...owner,
        walletAddress: owner.owner,
        owner: owner.username || owner.owner,
      }));

      setOwnerStats(updatedStats);
    } catch (err) {
      if (err?.code === 'permission-denied') {
        setError('Database permissions not configured yet. Update Firestore security rules and run the cron job from the admin panel.');
      } else {
        setError('Failed to load leaderboard. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();

    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sortColumns = [
    { id: 'totalNFTs', label: 'Total Mythics' },
    { id: 'uniqueNFTs', label: 'Unique Mythics' },
    { id: 'strikeRank', label: 'Rank Streak' },
    { id: 'highestRank', label: 'Highest Rank' },
    { id: 'totalRank', label: 'Total Rank' },
    { id: 'points', label: 'Power' },
  ];

  const sortedOwnerStats = [...ownerStats].sort((a, b) => {
    const isDesc = sortBy.desc ? -1 : 1;
    const aValue = a[sortBy.id];
    const bValue = b[sortBy.id];
    if (aValue < bValue) return -1 * isDesc;
    if (aValue > bValue) return 1 * isDesc;
    return 0;
  });

  const handleSort = (columnId) => {
    setSortBy(prev => ({
      id: columnId,
      desc: prev.id === columnId ? !prev.desc : true,
    }));
  };

  const getSortIndicator = (columnId) => {
    if (sortBy.id !== columnId) return null;
    return sortBy.desc ? ' ▼' : ' ▲';
  };

  const highlightRow = (owner) => {
    if (!walletAddress) return '';
    const rawAddress = owner.walletAddress || owner.owner;
    return rawAddress.toLowerCase() === walletAddress.toLowerCase() ? 'highlight' : '';
  };

  const formatWalletAddress = (address) => {
    if (address.length > 15 && windowWidth <= 768) {
      return `${address.slice(0, 5)}...${address.slice(-4)}`;
    }
    return address;
  };

  const holderUrl = (address) => `/holders/${address}`;

  const closeNotice = () => {
    if (typeof localStorage !== 'undefined') {
      if (dontShowAgain) {
        localStorage.setItem(LEADERBOARD_NOTICE_HIDE_KEY, '1');
      } else {
        localStorage.removeItem(LEADERBOARD_NOTICE_HIDE_KEY);
      }
    }
    setShowNotice(false);
  };

  if (error) {
    return (
      <div className="error-message">
        <div className="error-message-card">
          <p>{error}</p>
          <button onClick={loadData}>Retry</button>
        </div>
      </div>
    );
  }

  const filteredOwnerStats = searchTerm
    ? sortedOwnerStats.filter(o =>
        (o.owner || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (o.walletAddress || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : sortedOwnerStats;

  const skeletonWidths = [20, 120, 30, 30, 30, 30, 40, 40];

  return (
    <div className="Leaderboard">
      {showNotice && (
        <section className="leaderboard-notice page-notice-card" aria-label="Leaderboard methodology">
          <h3 className="leaderboard-notice-title">Mythic Holders Leaderboard</h3>
          <p>
            This is an experimental scoring model proposed to the team and the DAO.
          </p>
          <p>
            We want a score that does not reward only one playstyle. Total Rank and Total Mythics both
            lean toward the same signal: sheer haul, depth and volume. Rank Streak and Unique Mythics
            lean toward collector craft, locking consecutive ranks or spreading across many different
            Mythics.
          </p>
          <p>
            Power folds three inputs into a single score, giving a clean, balanced measure of collection
            strength:
          </p>
          <p className="leaderboard-notice-formula">
            Power = (Highest Rank × Rank Streak) + Total Rank
          </p>
          <p>
            Highest Rank × Rank Streak is multiplied because both are scarce. Top ranks are hardest to
            land, and long streaks take intent. Together they prevent low rank clutter from faking
            strength. Total Rank is added as the baseline, the summed rank across every copy, so total
            collecting always feeds the score.
          </p>
          <div className="leaderboard-notice-actions">
            <label className="leaderboard-notice-checkbox">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
              Don't show it again
            </label>
            <button className="leaderboard-notice-close" onClick={closeNotice}>Close</button>
          </div>
        </section>
      )}
      <div className="table-container">
        <table>
          <thead>
            <tr className="search-row">
              <th colSpan={8} className="search-th">
                <input
                  type="text"
                  placeholder="Search by owner or wallet..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="table-search-input"
                  aria-label="Search leaderboard"
                />
                {searchTerm ? (
                  <button
                    className="table-search-clear"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                  >✕</button>
                ) : (
                  <span className="table-search-icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </span>
                )}
              </th>
            </tr>
            <tr>
              <th className='position'>#</th>
              <th onClick={() => !loading && handleSort('owner')}>Owner</th>
              {sortColumns.map((column) => (
                <th key={column.id} onClick={() => !loading && handleSort(column.id)}>
                  {column.label}{!loading && getSortIndicator(column.id)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={i}>
                  {skeletonWidths.map((w, j) => (
                    <td key={j}>
                      <span className="skeleton" style={{ width: w, height: 16 }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : filteredOwnerStats.length === 0 && searchTerm ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '32px', opacity: 0.6 }}>
                  No owners match &ldquo;{searchTerm}&rdquo;
                </td>
              </tr>
            ) : (
              filteredOwnerStats.map((owner, index) => (
                <tr key={owner.walletAddress || owner.owner} className={highlightRow(owner)}>
                  <td>{index + 1}</td>
                  <td>
                    {owner.owner ? (
                      <span className="leaderboard-owner-cell">
                        <Link className="leaderboard-owner-link" to={holderUrl(owner.walletAddress || owner.owner)}>
                          {isWalletAddress(owner.owner) ? formatWalletAddress(owner.owner) : owner.owner}
                        </Link>
                        {(owner.walletAddress || owner.owner) && (
                          <button
                            className="leaderboard-copy-btn"
                            title="Copy full address"
                            onClick={() => handleCopyAddress(owner.walletAddress || owner.owner)}
                            aria-label="Copy wallet address"
                          >
                            {copiedAddress === (owner.walletAddress || owner.owner) ? '✓' : '⧉'}
                          </button>
                        )}
                      </span>
                    ) : 'No username found'}
                  </td>
                  <td>{owner.totalNFTs}</td>
                  <td>{owner.uniqueNFTs}</td>
                  <td>{owner.strikeRank}</td>
                  <td>{owner.highestRank}</td>
                  <td>{owner.totalRank}</td>
                  <td>{owner.points}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Leaderboard;
